import { v4 as uuidv4 } from "uuid";
import { NormalizeParkRedemption } from "@helpers/albumContractAdapter";

export default {
    methods: {
        /**
         * Gets albums by tag, park, and visit date.
         * Event services are filtered after AWS classifies albums by multimedia location.
         *
         * @param {Array<string>} tags Tags or race numbers.
         * @param {number} parkId Park identifier.
         * @param {string} visitDate Visit date in YYYY-MM-DD format.
         * @param {boolean} isUpdate Whether an existing album is being refreshed.
         * @param {Object} searchContext Dynamic search metadata.
         * @returns {Promise<void>}
         */
        async GetAlbumByTag(
            tags,
            parkId,
            visitDate,
            isUpdate = false,
            searchContext = {}
        ) {
            const searchParams = {
                Tags: tags,
                ParkId: parkId,
                VisitDate: visitDate,
            };

            const eventDetails = this.BuildEventDetails(searchContext);
            const serviceId = eventDetails?.ServiceId || "";

            const parameters = {
                type: 1,
                params: searchParams,
            };

            const response = await this.$store.dispatch("getAlbums", parameters);
            const filteredResponse = this.FilterAlbumsByConfiguredService(
                response,
                eventDetails
            );
            const hasExperiences = (
                Array.isArray(filteredResponse)
                    ? filteredResponse
                    : []
            ).some(
                (collection) =>
                    Array.isArray(collection?.PhotoExperiences) &&
                    collection.PhotoExperiences.length > 0
            );

            if (!hasExperiences) {
                Vue.$toast.open({
                    message: this.$t("Validations.tag_dont_have_multimedios"),
                    type: "error",
                });
                return;
            }

            if (isUpdate) {
                this.FormatUpdateAlbumsByTag(filteredResponse, { ...searchContext, Tags: tags });
            } else {
                this.FormatAlbumsByTag(filteredResponse, { ...searchContext, Tags: tags });
            }

            const albumIdx = this.getIdxParksByDateAndParkId(
                visitDate,
                parkId,
                serviceId || null
            );

            if (albumIdx > -1) {
                this.$store.dispatch("removeItemByTagCart", {
                    visitDate,
                    parkId,
                    serviceId: serviceId || null,
                });
            }

            this.$store.dispatch("setTagsTab", true);
            this.$store.dispatch("setTypeCodeTab", 1);
            this.$store.dispatch("setLoading", false);

            if (filteredResponse.length && this.$route.name !== "albumes") {
                this.$router.push({ name: "albumes" });
            }
        },

        /**
         * Returns the normalized service identifier of an API collection.
         *
         * The public GetAlbums contract keeps the common service identity in
         * PhotoDetails.Service and the quotation rules in each PhotoExperience.
         *
         * @param {Object} collection GetAlbums collection.
         * @returns {string} Uppercase service identifier or an empty string.
         */
        GetCollectionServiceId(collection = {}) {
            const parentServiceId = collection?.PhotoDetails?.Service?.Id;

            if (parentServiceId) {
                return String(parentServiceId).trim().toUpperCase();
            }

            const experience = Array.isArray(collection?.PhotoExperiences)
                ? collection.PhotoExperiences.find((item) => item?.ServiceId)
                : null;

            return String(experience?.ServiceId || "")
                .trim()
                .toUpperCase();
        },

        /**
         * Filters configured-service searches using EventSalesConfig.ServiceId.
         *
         * Generic tag searches preserve all collections. Event or custom searches
         * keep only the collection whose location-derived service matches the
         * service configured in EventSalesConfig.
         *
         * @param {Array<Object>} data GetAlbums response.
         * @param {Object|null} searchDetails Search configuration metadata.
         * @returns {Array<Object>} Matching album collections.
         */
        FilterAlbumsByConfiguredService(data, searchDetails = null) {
            if (!Array.isArray(data)) {
                return [];
            }

            const expectedServiceId = String(
                searchDetails?.ServiceId || ""
            ).trim().toUpperCase();

            // Only configured events (for example Bravest) are filtered by
            // EventSalesConfig.ServiceId. Fury keeps the historical tag flow:
            // AWS resolves the commercial service from the media location and
            // SUF preserves the returned collection without forcing a service.
            if (!searchDetails?.isEvent || !expectedServiceId) {
                return data;
            }

            return data.filter(
                (collection) =>
                    this.GetCollectionServiceId(collection) ===
                    expectedServiceId
            );
        },
        /**
         * Builds one normalized tag album for create and refresh flows.
         *
         * @param {Object} item API collection.
         * @param {Object} searchContext Dynamic search metadata.
         * @param {string} albumUuid Album identifier to preserve or create.
         * @returns {Object} Album ready for Vuex.
         */
        BuildTagAlbum(item, searchContext, albumUuid) {
            const photoDetails = item?.PhotoDetails || {};
            const tags = this.removeDuplicates(searchContext?.Tags || []);
            const eventDetails = this.BuildEventDetails(searchContext);
            const serviceId = eventDetails?.ServiceId || null;
            const experiences = Array.isArray(item?.PhotoExperiences)
                ? item.PhotoExperiences
                : [];

            experiences.forEach((experience) => {
                Vue.set(experience, "Tags", tags);
                Vue.set(experience, "isEvent", Boolean(eventDetails?.isEvent));
                Vue.set(experience, "eventDetails", eventDetails);
            });

            return {
                uuid: albumUuid,
                code: tags[0] || "",
                ParkId: photoDetails.ParkId,
                VisitDate: photoDetails.VisitDate,
                ExpiresDate: experiences[0]?.ExpiresDate || null,
                park: experiences,
                Tags: tags,
                Type: photoDetails.Type,
                parkRedemption: NormalizeParkRedemption(
                    photoDetails.ParkRedemption
                ),
                ServiceId: serviceId,
                isEvent: Boolean(eventDetails?.isEvent),
                eventDetails,
            };
        },

        /**
         * Formats tag search results and preserves dynamic event metadata.
         *
         * @param {Array<Object>} data API response.
         * @param {Object} searchContext Dynamic search metadata.
         * @returns {void}
         */
        FormatAlbumsByTag(data, searchContext = {}) {
            data.forEach((item) => {
                const album = this.BuildTagAlbum(
                    item,
                    searchContext,
                    uuidv4()
                );

                this.$store.dispatch("setAlbum", [album]);
            });
        },

        /**
         * Refreshes a tag album while preserving its search context.
         *
         * @param {Array<Object>} data API response.
         * @param {Object} searchContext Dynamic search metadata.
         * @returns {void}
         */
        FormatUpdateAlbumsByTag(data, searchContext = {}) {
            data.forEach((item) => {
                const idx = this.getIdxParksByUuid(this.deleteTags.uuid);
                const album = this.BuildTagAlbum(
                    item,
                    searchContext,
                    this.deleteTags.uuid
                );

                this.$store.dispatch("refreshAlbum", { idx, data: album });
            });
        },

        /**
         * Builds the minimal event object used by cart, quote and payment flows.
         *
         * @param {Object} searchContext Dynamic search metadata.
         * @returns {Object|null}
         */
        BuildEventDetails(searchContext = {}) {
            const serviceId = typeof searchContext?.ServiceId === "string"
                ? searchContext.ServiceId.trim()
                : "";
            const dates = Array.isArray(searchContext?.Dates)
                ? [...searchContext.Dates]
                : [];
            const isEvent = Boolean(searchContext?.isEvent && serviceId && dates.length);

            if (!serviceId && !isEvent) {
                return null;
            }

            return {
                EventId: searchContext?.EventId || null,
                ParkId: Number(searchContext?.ParkId) || null,
                Dates: dates,
                ServiceId: serviceId || null,
                AgentDetails: searchContext?.AgentDetails || null,
                UseEventDate: searchContext?.UseEventDate === true,
                MaxTags: Number(searchContext?.MaxTags) > 0
                    ? Number(searchContext.MaxTags)
                    : null,
                isEvent,
            };
        },

        /**
         * Merges tags with an existing album for the same park, date and service.
         *
         * @param {Array<string>} tag New tags.
         * @param {string} visitDate Visit date.
         * @param {number} parkId Park identifier.
         * @param {string|null} serviceId Optional service identifier.
         * @param {number|null} maxTags Maximum tags allowed for this search context.
         * @returns {Array<string>}
         */
        GroupParksByDate(tag, visitDate, parkId, serviceId = null, maxTags = null) {
            const normalizedServiceId = serviceId || null;
            const albums = Object.values(this.$store.state.albumes);
            const previousTags = albums
                .filter((item) => {
                    return (
                        item.Type === 1 &&
                        item.ParkId === parseInt(parkId) &&
                        item.VisitDate === visitDate.toString() &&
                        (item.ServiceId || null) === normalizedServiceId
                    );
                })
                .map((item) => item.Tags);

            const albumIdx = this.getIdxParksByDateAndParkId(
                visitDate,
                parkId,
                normalizedServiceId
            );

            if (albumIdx > -1) {
                this.$store.dispatch("deleteAlbum", albumIdx);
            }

            const requestedTags = this.removeDuplicates(
                Array.isArray(tag) ? tag : [tag]
            );
            const normalizedMaxTags = Number(maxTags);

            if (Number.isInteger(normalizedMaxTags) && normalizedMaxTags === 1) {
                return requestedTags.slice(0, 1);
            }

            const groupedTags = this.removeDuplicates(
                previousTags.concat(requestedTags).flat()
            );

            return Number.isInteger(normalizedMaxTags) && normalizedMaxTags > 0
                ? groupedTags.slice(0, normalizedMaxTags)
                : groupedTags;
        },

        /**
         * Finds an album by date, park and optional service.
         *
         * @param {string} date Visit date.
         * @param {number} parkId Park identifier.
         * @param {string|null} serviceId Optional service identifier.
         * @returns {number}
         */
        getIdxParksByDateAndParkId(date, parkId, serviceId = null) {
            return Object.values(this.$store.state.albumes).findIndex((item) => {
                return (
                    item.Type === 1 &&
                    item.ParkId === parseInt(parkId) &&
                    item.VisitDate === date.toString() &&
                    (item.ServiceId || null) === (serviceId || null)
                );
            });
        },
        /**
         * getIdxParksByUuid
         * @param {string} uuid
         * @return idx - Returns the index of the album
         */
        getIdxParksByUuid(uuid) {
            return Object.values(this.$store.state.albumes).findIndex(
                (item) => item.Type === 1 && item.uuid === uuid.toString()
            );
        },
        /**
         * removeDuplicates
         * @param {array} arr
         * @return arr - Returns an array without duplicates
         */
        removeDuplicates(arr) {
            return arr.filter((item, index) => arr.indexOf(item) === index);
        },
    },
};
